import './SendLink.scss';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import {
    ISharingInformation, ISharingLinkSettings, AccessStatus, ClientId, IEngagementExtraData
} from '../../interfaces/SharingInterfaces';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import * as PeoplePickerHelper from '../../utilities/PeoplePickerHelper';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import PeoplePicker from '../PeoplePicker/PeoplePicker';
import SendButton from '../SendButton/SendButton';
import SendButtonType from '../SendButton/SendButtonType';
import stringsResx from '../Share.resx';
import PlatformDetection from '@ms/odsp-utilities/lib/browser/PlatformDetection';
import { getEncodedMessageLength } from '../../utilities/ShareHelper';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';

import { ABExperiment, ABExpHash } from '@ms/odsp-utilities/lib/logging/ABExperiment';
import { Killswitch } from '@ms/odsp-utilities/lib/killswitch/Killswitch';

import FeatureOverrides from '@ms/odsp-utilities/lib/features/FeatureOverrides';
import * as ClientIdHelper from '../../utilities/ClientIdHelper';

// Feature flights
const MessageBorder = { ODB: 1175 };
const ForceMessageBorderVariation = { ODB: -1175 };

const SubjectLine = { ODB: 1193 };

export interface ISendLinkProps {
    ctaLabel?: string;
    currentSettings: ISharingLinkSettings;
    onChange?: (items: Array<any>) => void;
    onSendLinkClicked: (message: string, subject?: string) => void;
    sharingInformation: ISharingInformation;
    showTextArea?: boolean;
    onSelectedPeopleChange: (items: Array<any>) => void;
    groupsMemberCount: number;
    onViewPolicyTipClicked: () => void;
    linkRecipients: Array<IPerson>;
    permissionsMap: { [index: string]: AccessStatus };
    messageText: string;
    subjectText?: string;
    onSendLinkMessageChange: (messageText: string) => void;
    onSendLinkSubjectChange?: (subjectText: string) => void;
    createPeopleSharingLinks?: boolean;
    errorMessage: JSX.Element;
    clientId: ClientId;
    showSendButton: boolean;
    maximumMessageLength: number;
    maximumSubjectLength?: number;
    isLoadingDefaultRecipients: boolean;
    onManageExistingAccessClick?: () => void;
    engagementExtraData: IEngagementExtraData;
}

export interface ISendLinkState {
    messageLength: number;
    subjectLength: number;
}

export class SendLink extends React.Component<ISendLinkProps, ISendLinkState> {
    private _platformDetection: PlatformDetection;
    private _isOnExperiment: boolean;
    private _isSubjectExperimentOn: boolean;

    static contextTypes = {
        platformDetection: PropTypes.object.isRequired,
        sharingStore: PropTypes.object.isRequired
    };

    public refs: {
        [key: string]: React.ReactInstance,
        peoplePicker: HTMLDivElement
    };

    constructor(props: ISendLinkProps, context: any) {
        super(props);

        this._platformDetection = context.platformDetection;

        this.state = {
            messageLength: 0,
            subjectLength: 0
        };

        this._onManageExistingAccessClick = this._onManageExistingAccessClick.bind(this);

        this._isOnExperiment = false;
        this._isSubjectExperimentOn = false;

        // Experiment to test added border around message field - not being run on iOS
        if (FeatureOverrides.isFeatureEnabled(MessageBorder) && !this._platformDetection.isIOS && !(ClientIdHelper.isOfficeProduct(this.props.clientId) && this._platformDetection.isMac) && !Killswitch.isActivated('08cded93-1718-48b0-9b7e-bfdcf6384ef3', '7/17/2018', 'AddMessageBorder')) {
            const experiment = new ABExperiment({
                name: "AddMessageBorder",
                startDate: "07/17/2018",
                version: ABExpHash.v2
            });
            this._isOnExperiment = experiment.IsExperimentOn();
        }

        if (FeatureOverrides.isFeatureEnabled(SubjectLine)) {
            const subjectExperiment = new ABExperiment({
                name: "AddSubjectLine",
                startDate: "08/14/2018",
                version: ABExpHash.v2
            });
            this._isSubjectExperimentOn = subjectExperiment.IsExperimentOn();
        }
    }

    public componentWillReceiveProps(nextProps: ISendLinkProps) {
        PeoplePickerHelper.renderPickerError({
            selectedItems: nextProps.linkRecipients,
            sharingLinkKind: nextProps.currentSettings.sharingLinkKind,
            canAddExternalPrincipal: nextProps.sharingInformation.canAddExternalPrincipal,
            sharingInformation: nextProps.sharingInformation,
            viewPolicyTipCallback: nextProps.onViewPolicyTipClicked,
            permissionsMap: nextProps.permissionsMap
        });

        this.setState({
            ...this.state
        });
    }

    public render(): React.ReactElement<{}> {

        // Flight
        const isMessageBorderOn = this._isOnExperiment || FeatureOverrides.isFeatureEnabled(ForceMessageBorderVariation);

        const props = this.props;
        const currentSettings = props.currentSettings;
        const selectedItems = props.linkRecipients;
        const oversharingExternalsWarning = PeoplePickerHelper.getOversharingExternalsWarning(selectedItems);
        const oversharingGroupsWarning = PeoplePickerHelper.getOversharingGroupsWarning(selectedItems, props.groupsMemberCount);

        const cssClass = (this._platformDetection.isIOS || ClientIdHelper.isIos(this.props.clientId)) ?
            'od-SendLink-emailButtons-rounded' :
            ((this._platformDetection.isAndroid || ClientIdHelper.isAndroid(this.props.clientId)) ?
                'od-SendLink-emailButtons-allCaps' :
                'od-SendLink-emailButtons-left');
        const additionalMessageClass = isMessageBorderOn ? 'od-SendLink-message-border' : 'od-SendLink-message-no-border';

        const sendButton = this.props.showSendButton ? (
            <div className={ cssClass }>
                <SendButton
                    ctaLabel={ this.props.ctaLabel }
                    currentSettings={ this.props.currentSettings }
                    errorMessage={ this.props.errorMessage }
                    linkRecipients={ this.props.linkRecipients }
                    maximumMessageLength={ this.props.maximumMessageLength }
                    maximumSubjectLength={ this.props.maximumSubjectLength }
                    messageText={ this.props.messageText }
                    subjectText={ this.props.subjectText }
                    onSendLinkClicked={ this.props.onSendLinkClicked }
                    permissionsMap={ this.props.permissionsMap }
                    sendButtonType={ SendButtonType.button }
                    sharingInformation={ this.props.sharingInformation }
                />
            </div>
        ) : '';

        const peoplePicker = props.isLoadingDefaultRecipients ?
            (<div>
                <Spinner
                    className='od-SendLink-spinner'
                    label={ stringsResx.loadingRecipients }
                    size={ SpinnerSize.small }
                />
            </div>) :
            <PeoplePicker
                defaultSelectedItems={ selectedItems }
                error={ props.errorMessage }
                onChange={ props.onSelectedPeopleChange }
                onResolvedSuggestions={ this._onResolvedSuggestions }
                oversharingExternalsWarning={ oversharingExternalsWarning }
                oversharingGroupsWarning={ oversharingGroupsWarning }
                pickerSettings={ props.sharingInformation.peoplePickerSettings }
                sharingLinkKind={ currentSettings.sharingLinkKind }
                sharingInformation={ props.sharingInformation }
                permissionsMap={ props.permissionsMap }
                clientId={ props.clientId }
            />;
        return (
            <div className='od-SendLink'>
                <div className='od-SendLink-email'>
                    { peoplePicker }
                    <div className={ `od-SendLink-message ${additionalMessageClass}` }>
                        { this._renderTextArea() }
                        { this._renderCharacterCounter() }
                        { sendButton }
                    </div>
                </div>
            </div>
        );
    }

    private _onManageExistingAccessClick() {
        this.context.sharingStore.getEngagementHelper().whoElseHasAccessClicked(this.props.engagementExtraData);
        this.props.onManageExistingAccessClick();
    }

    private _renderTextArea(): JSX.Element {
        if (this.props.showTextArea) {
            return (
                <div>
                    { this._isSubjectExperimentOn &&
                        <TextField
                            underlined
                            placeholder={ stringsResx.subjectPlaceholder }
                            onGetErrorMessage={ this._validateSubject }
                            defaultValue={ this.props.subjectText }
                        />
                    }
                    <TextField
                        placeholder={ stringsResx.messagePlaceholder }
                        ariaLabel={ stringsResx.messageAriaLabel }
                        multiline resizable={ false }
                        onGetErrorMessage={ this._validateMessage }
                        defaultValue={ this.props.messageText }
                    />
                </div>
            );
        }
    }

    private _renderCharacterCounter(): JSX.Element {
        const messageLength = this.state.messageLength;
        const isError = messageLength > this.props.maximumMessageLength;
        const classes = `od-SendLink-characterCounter ${isError ? 'od-SendLink-characterCounter--error' : ''}`;

        if (messageLength > (this.props.maximumMessageLength / 2)) {
            return (
                <div className='od-SendLink-characterCounterWrapper'>
                    <span className={ classes }>{ messageLength }/{ this.props.maximumMessageLength }</span>
                </div>
            );
        }
    }

    @autobind
    private _validateMessage(value: string) {
        this.props.onSendLinkMessageChange(value);

        const messageLength = getEncodedMessageLength(value);
        this.setState({
            ...this.state,
            messageLength
        });

        return '';
    }

    @autobind
    private _validateSubject(value: string) {
        this.props.onSendLinkSubjectChange(value);

        const subjectLength = getEncodedMessageLength(value);
        this.setState({
            ...this.state,
            subjectLength
        });

        return value.length <= this.props.maximumSubjectLength ? '' : value.length + '/' + this.props.maximumSubjectLength;
    }

    @autobind
    private _onResolvedSuggestions(suggestions: IPerson[]): IPerson[] {
        this.context.sharingStore.getEngagementHelper().peoplePickerLinkedinSuggestions(this.props.engagementExtraData, suggestions);
        return suggestions;
    }
}